export interface DiasporaGroup {
  id: string;
  name: string;
  color: string;
  origin: string; // ISO Alpha-2 of origin country
  originName: string;
  population: number; // millions total diaspora
  factoid: string;
  destinations: {
    country: string;
    population: number; // thousands
  }[];
}

export const diasporaGroups: DiasporaGroup[] = [
  {
    id: "indian",
    name: "Indian Diaspora",
    color: "#f97316",
    origin: "IN",
    originName: "India",
    population: 18,
    factoid: "The world's largest diaspora — 18M people across 100+ countries. Gulf remittances alone exceed $40B/year, more than India's entire IT export revenue in 2000.",
    destinations: [
      { country: "US", population: 4400 },
      { country: "AE", population: 3500 },
      { country: "MY", population: 2000 },
      { country: "SA", population: 2400 },
      { country: "GB", population: 1800 },
      { country: "CA", population: 1300 },
      { country: "AU", population: 700 },
      { country: "ZA", population: 1300 },
      { country: "KW", population: 900 },
      { country: "QA", population: 700 },
      { country: "NZ", population: 220 },
      { country: "SG", population: 350 },
      { country: "TT", population: 500 },
      { country: "FJ", population: 330 },
      { country: "MU", population: 830 },
      { country: "KE", population: 100 },
      { country: "UG", population: 30 },
      { country: "TZ", population: 50 },
    ],
  },
  {
    id: "chinese",
    name: "Chinese Diaspora",
    color: "#ef4444",
    origin: "CN",
    originName: "China",
    population: 50,
    factoid: "50M overseas Chinese form the world's largest ethnic network. Southeast Asia's economies were built on their commerce — Indonesia, Thailand, and Malaysia all have Chinese-origin business elites who control outsized shares of GDP.",
    destinations: [
      { country: "ID", population: 10000 },
      { country: "TH", population: 7000 },
      { country: "MY", population: 6500 },
      { country: "SG", population: 2800 },
      { country: "US", population: 5000 },
      { country: "CA", population: 1700 },
      { country: "AU", population: 1200 },
      { country: "GB", population: 500 },
      { country: "VN", population: 1000 },
      { country: "MM", population: 1700 },
      { country: "PH", population: 1500 },
      { country: "PE", population: 200 },
      { country: "ZA", population: 350 },
      { country: "NZ", population: 230 },
      { country: "FR", population: 700 },
      { country: "DE", population: 200 },
      { country: "RU", population: 1000 },
    ],
  },
  {
    id: "african",
    name: "African Diaspora",
    color: "#84cc16",
    origin: "NG",
    originName: "Africa (broadly)",
    population: 40,
    factoid: "The transatlantic slave trade moved 12M Africans across the ocean between 1500–1900. Their descendants built the Americas. Brazil alone has the second-largest African-descent population in the world after Nigeria.",
    destinations: [
      { country: "US", population: 46000 },
      { country: "BR", population: 55000 },
      { country: "GB", population: 1200 },
      { country: "FR", population: 4000 },
      { country: "CA", population: 1200 },
      { country: "DE", population: 1000 },
      { country: "CO", population: 4000 },
      { country: "CU", population: 700 },
      { country: "JM", population: 3000 },
      { country: "TT", population: 1100 },
      { country: "HT", population: 9000 },
      { country: "DO", population: 2000 },
      { country: "AU", population: 400 },
      { country: "NL", population: 500 },
      { country: "BE", population: 500 },
      { country: "PT", population: 600 },
    ],
  },
  {
    id: "jewish",
    name: "Jewish Diaspora",
    color: "#3b82f6",
    origin: "IL",
    originName: "Israel / Historical",
    population: 8,
    factoid: "2,000 years of forced dispersal — from Babylon to Iberia to Eastern Europe to America. Each wave reshaped the center of gravity of global Jewish culture. The US now holds more Jewish people than Israel itself.",
    destinations: [
      { country: "US", population: 7200 },
      { country: "FR", population: 480 },
      { country: "CA", population: 400 },
      { country: "AR", population: 280 },
      { country: "RU", population: 165 },
      { country: "GB", population: 320 },
      { country: "AU", population: 130 },
      { country: "DE", population: 200 },
      { country: "BR", population: 130 },
      { country: "UA", population: 56 },
      { country: "ZA", population: 70 },
      { country: "HU", population: 100 },
    ],
  },
  {
    id: "lebanese",
    name: "Lebanese Diaspora",
    color: "#10b981",
    origin: "LB",
    originName: "Lebanon",
    population: 15,
    factoid: "1 in 4 Lebanese lives abroad — a ratio unmatched by any comparably sized nation. Brazil alone holds more Lebanese-origin people than Lebanon itself. War, economic collapse, and political paralysis have been the engines.",
    destinations: [
      { country: "BR", population: 7000 },
      { country: "AR", population: 1200 },
      { country: "MX", population: 400 },
      { country: "CO", population: 3000 },
      { country: "AU", population: 350 },
      { country: "US", population: 500 },
      { country: "CA", population: 250 },
      { country: "SN", population: 100 },
      { country: "CI", population: 150 },
      { country: "NG", population: 80 },
      { country: "FR", population: 120 },
      { country: "DE", population: 80 },
    ],
  },
  {
    id: "mexican",
    name: "Mexican Diaspora",
    color: "#8b5cf6",
    origin: "MX",
    originName: "Mexico",
    population: 12,
    factoid: "98% of the Mexican diaspora lives in a single country: the United States. The US-Mexico corridor is the world's largest bilateral migration system — $60B in annual remittances makes it Mexico's largest foreign income source.",
    destinations: [
      { country: "US", population: 11800 },
      { country: "CA", population: 100 },
      { country: "DE", population: 20 },
      { country: "ES", population: 70 },
      { country: "GT", population: 30 },
    ],
  },
  {
    id: "irish",
    name: "Irish Diaspora",
    color: "#34d399",
    origin: "IE",
    originName: "Ireland",
    population: 80,
    factoid: "80M people claim Irish descent — 16× Ireland's actual population. Famine, colonial extraction, and economic despair between 1840–1920 created the modern Irish-American identity, which now shapes US foreign policy toward Europe.",
    destinations: [
      { country: "US", population: 35000 },
      { country: "GB", population: 700 },
      { country: "AU", population: 2500 },
      { country: "CA", population: 4500 },
      { country: "NZ", population: 600 },
      { country: "AR", population: 500 },
      { country: "ZA", population: 100 },
    ],
  },
  {
    id: "romani",
    name: "Romani Diaspora",
    color: "#f59e0b",
    origin: "IN",
    originName: "South Asia (Historical Origin)",
    population: 12,
    factoid: "Stateless for 1,000 years. Romani people left northwest India around 1000 CE, spread westward through Persia and the Byzantine Empire. They remain Europe's largest ethnic minority — and its most persistently marginalized.",
    destinations: [
      { country: "RO", population: 2500 },
      { country: "HU", population: 900 },
      { country: "BG", population: 800 },
      { country: "SK", population: 500 },
      { country: "ES", population: 800 },
      { country: "RS", population: 600 },
      { country: "CZ", population: 250 },
      { country: "TR", population: 2700 },
      { country: "MK", population: 200 },
      { country: "GR", population: 300 },
      { country: "FR", population: 500 },
      { country: "DE", population: 200 },
      { country: "GB", population: 200 },
      { country: "US", population: 1000 },
      { country: "BR", population: 800 },
      { country: "RU", population: 200 },
    ],
  },
];
